import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pinned first in every picker; the rest of the 157 ISO-4217 currencies follow alphabetically.
export const COMMON_CURRENCIES = [
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', flag: '🇳🇬' },
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
  { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi', flag: '🇬🇭' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', flag: '🇰🇪' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand', flag: '🇿🇦' },
  { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling', flag: '🇹🇿' },
  { code: 'UGX', symbol: 'USh', name: 'Ugandan Shilling', flag: '🇺🇬' },
  { code: 'RWF', symbol: 'FRw', name: 'Rwandan Franc', flag: '🇷🇼' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', flag: '🇨🇦' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', flag: '🇦🇪' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', flag: '🇨🇳' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵' },
];

let cache = null;
let inflight = null;

const fetchAll = () => {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = axios.get(`${API}/fx/currencies`)
      .then((r) => {
        const list = Array.isArray(r.data?.currencies) ? r.data.currencies : [];
        cache = list.map((c) => ({ code: c.code, name: c.name || c.code, symbol: c.symbol || c.code, decimals: c.decimals ?? 2 }));
        return cache;
      })
      .catch(() => COMMON_CURRENCIES)
      .finally(() => { inflight = null; });
  }
  return inflight;
};

/** All platform currencies, common ones first. `pin` forces a code (e.g. business currency) to the top. */
export const useCurrencies = (pin) => {
  const [all, setAll] = useState(cache || COMMON_CURRENCIES);
  useEffect(() => {
    let live = true;
    fetchAll().then((list) => { if (live) setAll(list); });
    return () => { live = false; };
  }, []);
  const byCode = new Map(all.map((c) => [c.code, c]));
  const commonCodes = COMMON_CURRENCIES.map((c) => c.code);
  const ordered = [];
  const seen = new Set();
  const push = (c) => { if (c && !seen.has(c.code)) { seen.add(c.code); ordered.push(c); } };
  if (pin) push(byCode.get(pin) || COMMON_CURRENCIES.find((c) => c.code === pin) || { code: pin, name: pin, symbol: pin });
  commonCodes.forEach((code) => push(byCode.get(code) || COMMON_CURRENCIES.find((c) => c.code === code)));
  all.forEach(push);
  return { currencies: ordered, byCode, loaded: !!cache, commonCount: commonCodes.length + (pin ? 1 : 0) };
};

export default useCurrencies;
